import { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { Check, Copy, InfoIcon } from 'lucide-react';
import { MetaApp } from '../types/meta.types';

interface WebhookConfigDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    app: MetaApp | null;
}

interface CopyFieldProps {
    label: string;
    value: string | null | undefined;
    fieldKey: string;
    copiedKey: string | null;
    onCopy: (value: string, key: string) => void;
    placeholder?: string;
    monospace?: boolean;
}

function CopyField({
    label,
    value,
    fieldKey,
    copiedKey,
    onCopy,
    placeholder,
    monospace = true,
}: CopyFieldProps) {
    const display = value || placeholder || '';
    const isCopied = copiedKey === fieldKey;
    const canCopy = !!value;

    return (
        <div className="space-y-1.5">
            <Label className="text-gray-700 font-medium text-xs">{label}</Label>
            <div className="flex items-stretch gap-2">
                <div
                    className={`flex-1 min-w-0 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs ${
                        monospace ? 'font-mono' : ''
                    } ${value ? 'text-gray-900' : 'text-gray-400'} overflow-x-auto whitespace-nowrap leading-5`}
                    title={display}
                >
                    {display}
                </div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => canCopy && onCopy(value!, fieldKey)}
                    disabled={!canCopy}
                    className="shrink-0 border-gray-300 h-9 w-9 p-0"
                    title="Copiar"
                >
                    {isCopied ? (
                        <Check className="h-4 w-4 text-green-600" />
                    ) : (
                        <Copy className="h-4 w-4" />
                    )}
                </Button>
            </div>
        </div>
    );
}

export default function WebhookConfigDialog({
    open,
    onOpenChange,
    app,
}: WebhookConfigDialogProps) {
    const { toast } = useToast();
    const [copiedKey, setCopiedKey] = useState<string | null>(null);

    if (!app) return null;

    const handleCopy = async (value: string, key: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(key);
            toast({
                title: 'Copiado!',
                description: 'O valor foi copiado para a área de transferência.',
            });
            setTimeout(() => setCopiedKey((current) => (current === key ? null : current)), 1500);
        } catch {
            toast({
                variant: 'destructive',
                title: 'Erro ao copiar',
                description: 'Não foi possível acessar a área de transferência.',
            });
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[760px] gap-4">
                <DialogHeader className="space-y-1">
                    <DialogTitle className="text-xl font-bold text-[#0f172a]">
                        Configuração do Webhook
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-600">
                        Dados para configurar o webhook da app{' '}
                        <strong>{app.app_name}</strong> no Meta Developers.
                    </DialogDescription>
                </DialogHeader>

                <Alert className="border-[#7dd3fc] bg-[#7dd3fc]/5 py-2.5">
                    <InfoIcon className="h-4 w-4 text-[#0f172a]" />
                    <AlertDescription className="text-xs text-gray-700">
                        <strong className="font-semibold">Como configurar:</strong> No Meta Developers
                        cole a <strong>URL do Webhook</strong> e o <strong>Verify Token</strong> em{' '}
                        <em>Produtos → Webhooks</em>. Em <em>Facebook Login → Configurações</em>,
                        adicione o <strong>Domínio de Redirect</strong> às URLs válidas.
                    </AlertDescription>
                </Alert>

                <div className="space-y-3">
                    <CopyField
                        label="App ID"
                        value={app.app_id}
                        fieldKey="app_id"
                        copiedKey={copiedKey}
                        onCopy={handleCopy}
                    />

                    <CopyField
                        label="URL do Webhook"
                        value={app.webhook_url}
                        fieldKey="webhook_url"
                        copiedKey={copiedKey}
                        onCopy={handleCopy}
                        placeholder="Configure WEBHOOK_BASE_URL no backend para gerar a URL"
                    />

                    <CopyField
                        label="Verify Token"
                        value={app.verify_token}
                        fieldKey="verify_token"
                        copiedKey={copiedKey}
                        onCopy={handleCopy}
                        placeholder="Token não disponível"
                    />

                    <CopyField
                        label="Domínio de Redirect"
                        value={app.redirect_uri}
                        fieldKey="redirect_uri"
                        copiedKey={copiedKey}
                        onCopy={handleCopy}
                    />
                </div>

                <DialogFooter>
                    <Button
                        type="button"
                        onClick={() => onOpenChange(false)}
                        className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    >
                        Fechar
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
